import type {
  CoverageProjectOption,
  SubstituteSelection,
} from '../../services/api/timeOffCoverage';

/**
 * MKS-361 — the approver's per-project substitute pickers.
 *
 * Petra, 2026-10-19: the employee does NOT choose their own stand-in. This is
 * therefore the PM's control, reached from the approval queue's Coverage cell
 * (`EditSubstitutesModal`) both before and after the request is approved. The
 * requester's form shows the same projects as a flat "Affects:" line with no
 * control at all.
 *
 * Controlled: the parent owns the selections so it can PUT them. "Nobody" is
 * the empty option, and it is the default — a substitute is optional, and a
 * request nobody covers is still a valid request.
 *
 * A project the signed-in user does not manage (`canEdit: false`) renders as
 * read-only text rather than a disabled dropdown: the server refuses a write
 * there, so offering a control that cannot be saved would be a lie. The
 * read-only projects are also the reason the parent must not send them back —
 * see `EditSubstitutesModal`.
 */

interface SubstitutePickerProps {
  projects: CoverageProjectOption[];
  value: SubstituteSelection[];
  onChange: (next: SubstituteSelection[]) => void;
  disabled?: boolean;
}

/** The selection for one project, or undefined when nobody is picked. */
function selectionFor(
  value: SubstituteSelection[],
  projectId: number,
): SubstituteSelection | undefined {
  return value.find((s) => s.projectId === projectId);
}

/**
 * What the PM needs to know about a candidate in one line: are they already
 * on this project's team, and (for a pick that has gone stale) are they away
 * themselves.
 */
function candidateLabel(candidate: CoverageProjectOption['candidates'][number]): string {
  const origin = candidate.source === 'project' ? 'on the team' : 'same role';
  return `${candidate.name} — ${origin}${candidate.awayDuringRequest ? ', away too' : ''}`;
}

export default function SubstitutePicker({
  projects,
  value,
  onChange,
  disabled,
}: SubstitutePickerProps) {
  if (projects.length === 0) return null;

  const update = (projectId: number, patch: Partial<SubstituteSelection> & { clear?: boolean }) => {
    const others = value.filter((s) => s.projectId !== projectId);
    if (patch.clear) {
      onChange(others);
      return;
    }
    const current = selectionFor(value, projectId);
    const substituteUserId = patch.substituteUserId ?? current?.substituteUserId;
    if (substituteUserId === undefined) {
      onChange(others);
      return;
    }
    const note = patch.note !== undefined ? patch.note : (current?.note ?? null);
    onChange([...others, { projectId, substituteUserId, note }]);
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Who covers them</h4>
        <p className="text-sm text-gray-500">
          One substitute per project, optional. Only people in the same role who are available on
          these days are offered — someone from the team first, then anyone carrying the same role.
        </p>
      </div>

      {projects.map((project) => {
        const selected = selectionFor(value, project.projectId);
        const selectId = `substitute-${project.projectId}`;
        const selectedCandidate = project.candidates.find(
          (c) => c.userId === selected?.substituteUserId,
        );
        /**
         * A stored substitute who is no longer a candidate at all — they left
         * the project and lost the role tag. Named rather than dropped, so
         * the PM sees who is on the request before they replace them.
         */
        const strandedName =
          !selectedCandidate && selected && project.selected?.substituteUserId === selected.substituteUserId
            ? project.selected.substituteName
            : null;

        return (
          <div
            key={project.projectId}
            className="rounded-lg border border-gray-200 px-4 py-3 space-y-2"
          >
            <div className="flex items-baseline justify-between gap-2">
              {/* A read-only project has no control, so it gets no label. */}
              {project.canEdit ? (
                <label htmlFor={selectId} className="text-sm font-medium text-gray-900">
                  {project.projectName}
                </label>
              ) : (
                <span className="text-sm font-medium text-gray-900">{project.projectName}</span>
              )}
              {project.roleName && (
                <span className="text-xs text-gray-500">role: {project.roleName}</span>
              )}
            </div>

            {project.canEdit ? (
              <select
                id={selectId}
                value={selected?.substituteUserId ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  update(project.projectId, {
                    ...(e.target.value === ''
                      ? { clear: true }
                      : { substituteUserId: Number(e.target.value) }),
                  })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              >
                <option value="">No substitute</option>
                {strandedName && selected && (
                  <option value={selected.substituteUserId}>
                    {strandedName} — no longer in this role
                  </option>
                )}
                {project.candidates.map((candidate) => (
                  <option key={candidate.userId} value={candidate.userId}>
                    {candidateLabel(candidate)}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-900">
                {selectedCandidate ? (
                  candidateLabel(selectedCandidate)
                ) : strandedName ? (
                  `${strandedName} — no longer in this role`
                ) : (
                  <span className="text-gray-400">No substitute</span>
                )}{' '}
                <span className="text-xs text-gray-500">
                  — only this project’s manager can change it
                </span>
              </p>
            )}

            {/* The note is only worth asking for once somebody is picked. */}
            {project.canEdit && selected && (
              <input
                type="text"
                maxLength={500}
                disabled={disabled}
                aria-label={`Note for the substitute on ${project.projectName}`}
                placeholder="Note for them (optional)"
                value={selected.note ?? ''}
                onChange={(e) => update(project.projectId, { note: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />
            )}

            {project.canEdit && project.candidates.length === 0 && (
              <p className="text-sm text-gray-500">
                Nobody in this role is available on these days.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
